"use client";

import type { PracticeGroup, PracticeHand } from "../practice";
import { handColorOf } from "./practice-keyboard";
import { KEYBOARD_MAX_W_PX } from "./panel-metrics";

/**
 * The silhouette of a run: one rail per hand, one cell per onset.
 *
 * A percentage bar says how far along; it says nothing about what is coming,
 * which is the only thing worth knowing about two hands. Here the player sees
 * the shape of the unit before playing it — "the left alone for four, then
 * together, then the right leaves" — and can find their place again after a
 * mistake, which a smooth gradient does not allow.
 *
 * It also replaces the move markers of the single-hand run, which cannot
 * exist with two: measured, only **15.0%** of left-hand displacements land on
 * the same group as a right-hand one, so there is no single "your hand moves".
 *
 * ## Two metrics, and the difference is the step
 *
 * `even` gives every onset the same width. That is right for the runs without
 * rhythm: there, an onset *is* one step, and drawing durations would assert a
 * timing the step deliberately does not ask for.
 *
 * `timed` gives every onset the width of its own duration, and rules the
 * rail with the beats and bars underneath. In the step whose whole subject is
 * duration, equal cells are not neutral — they are wrong, and they are wrong
 * about the one thing being taught.
 *
 * This is the exception to the arbitration that rejected proportional widths
 * on S0's frieze, and the distinction is what makes it one: width cannot
 * carry duration **when the cell contains something** (S0's cells hold
 * keyboards, which then changed size and read as a bug). It can, and must,
 * when the rail carries nothing but time.
 *
 * ## A rail lights where the hand sounds, not where it strikes
 *
 * The cells are cut on the *union* of both hands' attacks, since the two
 * rails have to line up. A hand holding semibreves under a hand playing
 * quavers therefore owns one cell in four and used to light only that one:
 * four held chords, four specks, on the step whose instruction to that hand
 * is *do not let go*. A cell is now filled when the hand is sounding there,
 * struck or still held (`sustainByGroup`), and consecutive sounding cells
 * are welded into one bar. The 2 px break is kept for attacks only, so it
 * stops meaning "cell" and means what it should: a key going down.
 */

const RAIL_H_PX = 3;
const RAIL_GAP_PX = 3;
const RULER_H_PX = 10;
const BEAT_TICK_H_PX = 6;
const BAR_TICK_H_PX = 10;

/**
 * Contrast of the ruler against the panel.
 *
 * 0.34 puts a hairline at 3.0:1 on the panel's ground, the floor WCAG 1.4.11
 * asks of a graphic that carries meaning. The bar marks go to 0.5 because
 * they carry more: a bar is where a player restarts.
 */
const BEAT_TICK_ALPHA = 0.34;
const BAR_TICK_ALPHA = 0.5;

/** The span a timed rail measures against. */
export type RunSpan = {
  readonly startTick: number;
  readonly endTick: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
};

export function RunMap({
  groups,
  hands,
  currentIndex,
  span,
  honoured,
  sustaining,
}: {
  readonly groups: readonly PracticeGroup[];
  /** Rails to draw. One during an accompanied pass: the run is the player's. */
  readonly hands: readonly PracticeHand[];
  /**
   * Cells before this index read as already gone by, the cell on it is
   * current. Omit for a **still** map — the shape of the unit, read before
   * playing rather than followed during — where every cell is at full
   * strength and nothing is "not yet".
   */
  readonly currentIndex?: number;
  /** Given: cells are as wide as they are long, and the rail is ruled. */
  readonly span?: RunSpan;
  /**
   * Per-onset flags from a finished pass. A cell the player never answered
   * stays hollow — that is the whole report, and it needs no number.
   */
  readonly honoured?: readonly boolean[];
  /**
   * Per-onset, the hands holding a key the onset does not strike — what
   * `sustainByGroup` reads off the score. Omitted, the rail falls back to
   * lighting attacks alone, which is only ever right for a single hand.
   */
  readonly sustaining?: ReadonlyArray<readonly PracticeHand[]>;
}) {
  const total = groups.length;

  if (total === 0) {
    return null;
  }

  const still = currentIndex === undefined;
  const widths = cellWidths(groups, span);
  // Below a legible width the cells stop being separated, so the rail stays a
  // rail instead of becoming dust.
  const gap = KEYBOARD_MAX_W_PX / total >= 8 ? 2 : 0;
  // Carved out of the cell rather than laid between cells: a `gap` on the row
  // would be spent once per break, and a hand that welds three cells into one
  // bar has fewer breaks than the other. The two rails would then distribute
  // the same widths over different remainders and stop lining up, which is
  // the one thing the map is for.
  const sounds = (index: number, hand: PracticeHand): boolean =>
    groups[index].notes.some((n) => n.hand === hand) ||
    (sustaining?.[index]?.includes(hand) ?? false);
  const holdsOver = (index: number, hand: PracticeHand): boolean =>
    sustaining?.[index]?.includes(hand) ?? false;

  const railProps = still
    ? ({ role: "img", "aria-label": railLabel(groups, hands) } as const)
    : ({
        role: "progressbar",
        "aria-valuemin": 0,
        "aria-valuemax": total,
        "aria-valuenow": Math.min(currentIndex, total),
        "aria-label": "Avancement, une voie par main",
      } as const);

  return (
    <div style={{ marginBottom: 8 }}>
      <div {...railProps} style={{ display: "grid", gap: RAIL_GAP_PX }}>
        {hands.map((hand) => (
          <div
            key={hand}
            style={{ display: "flex", height: RAIL_H_PX, width: "100%" }}
          >
            {groups.map((group, index) => (
              <Cell
                key={index}
                hand={hand}
                group={group}
                grow={widths[index]}
                sustains={holdsOver(index, hand)}
                joinsPrevious={index > 0 && holdsOver(index, hand) && sounds(index - 1, hand)}
                joinsNext={
                  index + 1 < total &&
                  sounds(index, hand) &&
                  holdsOver(index + 1, hand)
                }
                gap={index + 1 === total ? 0 : gap}
                passed={still || index < currentIndex}
                current={!still && index === currentIndex}
                missed={honoured !== undefined && honoured[index] === false}
              />
            ))}
          </div>
        ))}
      </div>

      {span !== undefined && <Ruler span={span} />}
    </div>
  );
}

function Cell({
  hand,
  group,
  grow,
  sustains,
  joinsPrevious,
  joinsNext,
  gap,
  passed,
  current,
  missed,
}: {
  hand: PracticeHand;
  group: PracticeGroup;
  grow: number;
  /** The hand holds a key here without striking one. */
  sustains: boolean;
  joinsPrevious: boolean;
  joinsNext: boolean;
  /** Break to the next cell, carved out of this one's own width. */
  gap: number;
  passed: boolean;
  current: boolean;
  missed: boolean;
}) {
  const strikes = group.notes.some((n) => n.hand === hand);
  const sounds = strikes || sustains;
  const color = handColorOf(hand);
  const left = joinsPrevious ? 0 : 999;
  const right = joinsNext ? 0 : 999;
  // A break is only ever spent where the bar really ends: a welded cell keeps
  // its neighbour's width, so both rails still divide the row identically.
  const slot: React.CSSProperties = {
    flexGrow: grow,
    flexBasis: 0,
    minWidth: 0,
    paddingRight: joinsNext ? 0 : gap,
  };

  if (!sounds) {
    return (
      <span style={slot}>
        <span
          style={{
            display: "block",
            height: "100%",
            borderRadius: 999,
            background: "rgba(255, 255, 255, 0.06)",
          }}
        />
      </span>
    );
  }

  // Hollow is a verdict on an attack. A cell the hand was only holding
  // through has no attack to have missed, and is drawn as what it is.
  const hollow = missed && strikes;

  return (
    <span style={slot}>
      <span
        style={{
          display: "block",
          height: "100%",
          borderRadius: `${left}px ${right}px ${right}px ${left}px`,
          // A cell the pass went past without an answer is drawn hollow rather
          // than in another colour: the rail already means "this hand strikes
          // here", and a second hue on it would have to mean a second thing.
          background: hollow ? "transparent" : color,
          border: hollow ? `1px solid ${color}` : "none",
          opacity: hollow ? 0.55 : passed ? 1 : 0.3,
          boxShadow: current ? `0 0 6px ${color}` : "none",
          transition: "opacity 0.2s ease-out",
        }}
      />
    </span>
  );
}

/**
 * Beats and bars under the rail — the "manifestly measured" channel that
 * makes proportional widths readable instead of merely uneven.
 */
function Ruler({ span }: { span: RunSpan }) {
  const length = span.endTick - span.startTick;

  if (length <= 0 || span.ticksPerBeat <= 0) {
    return null;
  }

  const marks: { left: number; bar: boolean }[] = [];

  for (
    let tick = span.startTick;
    tick <= span.endTick;
    tick += span.ticksPerBeat
  ) {
    marks.push({
      left: ((tick - span.startTick) / length) * 100,
      bar:
        span.ticksPerBar > 0 &&
        (tick - span.startTick) % span.ticksPerBar === 0,
    });
  }

  return (
    <div
      aria-hidden
      style={{ position: "relative", height: RULER_H_PX, marginTop: 3 }}
    >
      {marks.map((mark, index) => (
        <span
          key={index}
          style={{
            position: "absolute",
            left: `${mark.left}%`,
            top: 0,
            width: 1,
            height: mark.bar ? BAR_TICK_H_PX : BEAT_TICK_H_PX,
            background: `rgba(255, 255, 255, ${mark.bar ? BAR_TICK_ALPHA : BEAT_TICK_ALPHA})`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * How much of the rail each onset takes.
 *
 * Without a span every onset takes the same share. With one, an onset lasts
 * until the next, and the last one until the end of the unit — the duration a
 * player actually holds, not the duration the note is written for, which
 * would leave gaps the rail cannot explain.
 */
/** What a screen reader is told about a still map. */
function railLabel(
  groups: readonly PracticeGroup[],
  hands: readonly PracticeHand[],
): string {
  const per = hands.map((hand) => {
    const count = groups.filter((g) =>
      g.notes.some((n) => n.hand === hand),
    ).length;

    return `${count} à la main ${hand === "L" ? "gauche" : "droite"}`;
  });

  return `${groups.length} attaques : ${per.join(", ")}`;
}

function cellWidths(
  groups: readonly PracticeGroup[],
  span: RunSpan | undefined,
): readonly number[] {
  if (span === undefined) {
    return groups.map(() => 1);
  }

  return groups.map((group, index) => {
    const next =
      index + 1 < groups.length ? groups[index + 1].startTick : span.endTick;

    return Math.max(1, next - group.startTick);
  });
}
